import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BrowserModule, REMOVE_STYLES_ON_COMPONENT_DESTROY } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import { pharmacyCatalogueReducer } from 'app/shared/redux/pharmacy-catalogue/catalogue-item.reducer';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { GooglePlacesAutocompleteDirective } from 'app/shared/directives/google-places-autocomplete/google-places-autocomplete.directive';
import {
  PerfectScrollbarDirective,
  PERFECT_SCROLLBAR_CONFIG,
  PerfectScrollbarConfigInterface,
} from 'app/shared/directives/perfect-scrollbar/perfect-scrollbar.directive';

import { InMemoryWebApiModule } from 'angular-in-memory-web-api';
import { InMemoryDataService } from './shared/inmemory-db/inmemory-db.service';

import { rootRouterConfig } from './app.routing';
import { SharedModule } from './shared/shared.module';
import { AppComponent } from './app.component';

import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ErrorHandlerService } from './shared/services/error-handler.service';
import { TokenInterceptor } from './shared/interceptors/token.interceptor';
import { ComingSoonComponent } from './views/coming-soon/coming-soon/coming-soon.component';
import { ProfileComponent } from './views/profile/profile.component';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule, MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MAT_TABS_CONFIG, MatTabsModule } from '@angular/material/tabs';
import { AccessDeniedComponent } from './views/access-denied/access-denied.component';
import { SupportComponent } from './views/support/support.component';
import { MatIconModule } from '@angular/material/icon';

import { PortalSettingsService } from './shared/services/portal-settings.service';
import { config } from '../config';
import { QuestionsComponent } from './shared/components/questions/questions.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { MAT_PAGINATOR_DEFAULT_OPTIONS } from '@angular/material/paginator';
import { ReAuthenticateModule } from './shared/components/dialogs/re-authenticate/re-authenticate.module';
import { RequestParamsInterceptor } from './shared/interceptors/request-params.interceptor';
import { OtpDialogModule } from './shared/components/otp-dialog/otp-dialog.module';

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

function initializeAppFactory(portalSettingsService: PortalSettingsService) {
  return () => portalSettingsService.getPortalSettings();
}

function loadGoogleMapsFactory() {
  return () => new Promise<void>((resolve, reject) => {
    if ((window as any).google && (window as any).google.maps) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places`;
    script.async = false;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (error) => {
      console.error('Failed to load Google Maps API:', error);
      resolve();
    };
    document.head.appendChild(script);
  });
}

@NgModule({ declarations: [
        AppComponent,
        ComingSoonComponent,
        ProfileComponent,
        AccessDeniedComponent,
        SupportComponent,
        QuestionsComponent,
    ],
    bootstrap: [AppComponent], imports: [MatTableModule,
        MatSortModule,
        MatTabsModule,
        BrowserModule,
        BrowserAnimationsModule,
        SharedModule,
        PerfectScrollbarDirective,
        MatButtonModule,
        MatFormFieldModule,
        MatCardModule,
        MatDividerModule,
        MatInputModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSelectModule,
        FormsModule,
        MatTooltipModule,
        ReactiveFormsModule,
        MatProgressSpinnerModule,
        MatIconModule,
        ReAuthenticateModule,
        OtpDialogModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        NgxMaskDirective,
        InMemoryWebApiModule.forRoot(InMemoryDataService, { passThruUnknownUrl: true }),
        RouterModule.forRoot(rootRouterConfig, {
            useHash: false
        }),
        StoreModule.forRoot({ pharmacyCatalogue: pharmacyCatalogueReducer }),
        GooglePlacesAutocompleteDirective], providers: [
        // ngx-mask 15 dropped NgxMaskModule.forRoot(); the config now comes from a provider
        // and the directive is imported standalone.
        provideNgxMask(),
        {
            provide: APP_INITIALIZER,
            useFactory: loadGoogleMapsFactory,
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppFactory,
            deps: [PortalSettingsService],
            multi: true,
        },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        { provide: PERFECT_SCROLLBAR_CONFIG, useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG },
        //default snack bar duration
        { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 3000 } },
        // MDC draws a check/dash SVG inside the toggle handle and `hideIcon` defaults to
        // false; the legacy toggle this app was designed against drew no icon at all.
        { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { hideIcon: true } },
        // MDC button toggles draw a check mark in the selected button and shift its label
        // 30px; the legacy toggles had no indicator.
        { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: true, hideMultipleSelectionIndicator: true } },
        // Live (Angular 16) drew the paginator's page-size select as a legacy underline field; 'fill' is the MDC appearance the global underline rule restyles.
        { provide: MAT_PAGINATOR_DEFAULT_OPTIONS, useValue: { formFieldAppearance: 'fill' } },
        // Keep select pane width content-sized as in the live legacy UI.
        { provide: MAT_SELECT_CONFIG, useValue: { panelWidth: null } },
        // Live runs Angular 16, which never removed component styles once added: `::ng-deep` rules
        // from components such as the orders table (loaded on the post-login landing page) stay
        // active app-wide. Angular 17+ removes them on destroy; keep them, as live does.
        { provide: REMOVE_STYLES_ON_COMPONENT_DESTROY, useValue: false },
        // Legacy tabs sized to their labels; MDC stretches them by default.
        { provide: MAT_TABS_CONFIG, useValue: { stretchTabs: false } },
        // REQUIRED IF YOU USE JWT AUTHENTICATION
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RequestParamsInterceptor,
            multi: true
        },
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: {
                width: '100%',
                maxWidth: '500px',
                minWidth: '300px',
                hasBackdrop: true,
            },
        },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule { }
